// Shared delivery/handling rules for create-sale and edit-sale. These are a
// straight port of the storefront's cart.service.ts helpers, so a basket
// priced here comes out the same as it does at the live checkout - keep
// both copies in step when the rules change.

export function isStoneCladdingProduct(categorySlug: string): boolean {
  // Matches both the parent-resolved slug ("all-stone-cladding-panels") and
  // the leaf slug add-on lines carry ("white-stone-cladding-panels").
  if (!categorySlug) return false;
  return categorySlug.indexOf('stone-cladding-panels') >= 0 || categorySlug.indexOf('stone-cladding-corner-pieces') >= 0;
}

export function isPavingProduct(categorySlug: string, sku: string): boolean {
  // Stone cladding crate SKUs (e.g. "BE-SC-FL-CRATE") also contain "crate",
  // which would otherwise get counted as paving too.
  if (isStoneCladdingProduct(categorySlug)) return false;
  return categorySlug?.indexOf('paving') >= 0 || sku?.toLowerCase().indexOf('slab') >= 0 || sku?.toLowerCase().indexOf('crate') >= 0;
}

// 1 crate/pallet load = 48 cartons.
export const STONE_CLADDING_CARTONS_PER_CRATE = 48;

export function stoneCladdingCartons(item: any): number {
  // Prefer the line's format over its stored isFullCrate flag - baskets
  // saved in localStorage before the Carton fix still carry a stale `true`.
  const fullCrate = item.productFormat ? isFullCrateFormat(item.productFormat) : item.isFullCrate;
  return fullCrate ? STONE_CLADDING_CARTONS_PER_CRATE * item.quantity : item.quantity;
}

// Delivery: one postcode-rate charge per crate/pallet load.
export function getStoneCladdingLoads(cartons: number): number {
  return cartons > 0 ? Math.ceil(cartons / STONE_CLADDING_CARTONS_PER_CRATE) : 0;
}

function stoneCladdingHandlingFeeForLoad(cartonsInLoad: number): number {
  if (cartonsInLoad <= 4) return 30;
  if (cartonsInLoad <= 7) return 50;
  if (cartonsInLoad <= 12) return 70;
  if (cartonsInLoad <= 25) return 80;
  return 90; // 26-48 (a full crate)
}

// Handling: charged per load, each load priced on its own carton count.
export function getStoneCladdingHandlingFee(totalCartons: number): number {
  let remaining = totalCartons;
  let fee = 0;
  while (remaining > 0) {
    const load = Math.min(remaining, STONE_CLADDING_CARTONS_PER_CRATE);
    fee += stoneCladdingHandlingFeeForLoad(load);
    remaining -= load;
  }
  return fee;
}

// Formats sold loose rather than by the crate - same list as the
// storefront's product page. Back-office cart lines carry the format as a
// comma-joined list of attribute slugs (e.g. "white,carton"), and saved
// orders may carry the display value ("Carton"), so each token is checked
// case-insensitively.
const LOOSE_FORMATS = ['m²', '1m²', 'm2', '1m2', 'slab', 'carton', 'single panel', 'single-panel'];

export function isFullCrateFormat(format: string): boolean {
  if (!format) return true;
  const tokens = [format, ...format.split(',')].map(t => t.trim().toLowerCase());
  return !tokens.some(t => LOOSE_FORMATS.includes(t));
}
